// components/settings/SettingsLoyalty.jsx
import React, { useEffect, useState } from 'react'
import { apiFetch } from '../../api.js'

// ─── Segments ────────────────────────────────────────────────────────────────

const SEGMENTS = {
  champion:   { label: 'Champions',    emoji: '🥇', desc: 'Récents, fréquents, gros montants', color: 'emerald' },
  loyal:      { label: 'Fidèles',      emoji: '🥈', desc: 'Réguliers et récents',               color: 'blue'    },
  new:        { label: 'Nouveaux',     emoji: '🌱', desc: 'Premier achat récent',                color: 'violet'  },
  at_risk:    { label: 'À risque',     emoji: '⚠️', desc: "N'ont pas acheté depuis un moment",  color: 'amber'   },
  occasional: { label: 'Occasionnels', emoji: '🔄', desc: 'Acheteurs peu fréquents',             color: 'slate'   },
  lost:       { label: 'Perdus',       emoji: '💤', desc: 'Inactifs depuis longtemps',            color: 'rose'    },
}

const COLORS = {
  emerald: { border: 'border-emerald-200', bg: 'bg-emerald-50', label: 'text-emerald-800', input: 'focus:ring-emerald-300' },
  blue:    { border: 'border-blue-200',    bg: 'bg-blue-50',    label: 'text-blue-800',    input: 'focus:ring-blue-300'    },
  violet:  { border: 'border-violet-200',  bg: 'bg-violet-50',  label: 'text-violet-800',  input: 'focus:ring-violet-300'  },
  amber:   { border: 'border-amber-200',   bg: 'bg-amber-50',   label: 'text-amber-800',   input: 'focus:ring-amber-300'   },
  slate:   { border: 'border-slate-200',   bg: 'bg-slate-50',   label: 'text-slate-700',   input: 'focus:ring-slate-300'   },
  rose:    { border: 'border-rose-200',    bg: 'bg-rose-50',    label: 'text-rose-800',    input: 'focus:ring-rose-300'    },
}

const DEFAULT_SEGMENT = { discount_pct: 0, min_order: 0, note: '' }
const DISCOUNT_DEFAULTS = {
  champion:   { discount_pct: 10, min_order: 0,     note: 'Livraison gratuite' },
  loyal:      { discount_pct: 7,  min_order: 5000,  note: '' },
  new:        { discount_pct: 5,  min_order: 0,     note: 'Bienvenue !' },
  at_risk:    { discount_pct: 3,  min_order: 0,     note: 'On vous revient ?' },
  occasional: { discount_pct: 2,  min_order: 10000, note: '' },
  lost:       { discount_pct: 0,  min_order: 0,     note: '' },
}

const CRITERIA_DEFAULTS = {
  recency:   { s4: 30,     s3: 60,     s2: 120 },
  frequency: { s4: 10,     s3: 5,      s2: 2   },
  monetary:  { s4: 500000, s3: 100000, s2: 20000 },
}

function num(v, min = 0, max = 9999999) {
  const n = Number(v)
  return isNaN(n) ? min : Math.min(max, Math.max(min, n))
}

// ─── Composant critère ────────────────────────────────────────────────────────

function CriteriaRow({ label, unit, scores, onChange }) {
  return (
    <div className="grid grid-cols-[180px_1fr] gap-4 items-start py-3 border-b border-slate-100 last:border-0">
      <div>
        <div className="font-semibold text-slate-800 text-sm">{label}</div>
        <div className="text-[11px] text-slate-400 mt-0.5">{unit}</div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[
          { key: 's4', emoji: '🥇', label: 'Score 4', hint: scores.hint4 },
          { key: 's3', emoji: '🥈', label: 'Score 3', hint: scores.hint3 },
          { key: 's2', emoji: '🥉', label: 'Score 2', hint: scores.hint2 },
        ].map(({ key, emoji, label: lbl, hint }) => (
          <div key={key}>
            <label className="text-[11px] font-semibold text-slate-500 mb-1 flex items-center gap-1">
              <span>{emoji}</span> {lbl}
            </label>
            <div className="relative">
              <input
                type="number"
                min="0"
                value={scores[key]}
                onChange={e => onChange(key, e.target.value)}
                className="w-full border border-slate-300 rounded-xl px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-300 pr-2"
              />
            </div>
            <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Composant principal ──────────────────────────────────────────────────────

export default function SettingsLoyalty({ onFlash }) {
  const [config, setConfig] = useState(
    Object.fromEntries(Object.keys(SEGMENTS).map(k => [k, { ...DISCOUNT_DEFAULTS[k] }]))
  )
  const [criteria, setCriteria] = useState(CRITERIA_DEFAULTS)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savingCriteria, setSavingCriteria] = useState(false)

  useEffect(() => {
    Promise.all([
      apiFetch('/api/settings/rfm-discounts'),
      apiFetch('/api/settings/rfm-criteria'),
    ]).then(([discounts, crit]) => {
      // Discounts — support ancien format flat
      const merged = {}
      for (const k of Object.keys(SEGMENTS)) {
        const v = discounts[k]
        if (typeof v === 'object' && v !== null) {
          merged[k] = { ...DEFAULT_SEGMENT, ...v }
        } else if (typeof v === 'number') {
          merged[k] = { ...DEFAULT_SEGMENT, discount_pct: v }
        } else {
          merged[k] = { ...DEFAULT_SEGMENT, ...DISCOUNT_DEFAULTS[k] }
        }
      }
      setConfig(merged)
      // Critères
      if (crit) setCriteria({ ...CRITERIA_DEFAULTS, ...crit })
    })
    .catch(() => {})
    .finally(() => setLoading(false))
  }, [])

  function update(seg, field, value) {
    setConfig(c => ({ ...c, [seg]: { ...c[seg], [field]: value } }))
  }

  function updateCriteria(dim, key, value) {
    setCriteria(c => ({ ...c, [dim]: { ...c[dim], [key]: Number(value) || 0 } }))
  }

  async function saveDiscounts() {
    setSaving(true)
    try {
      const payload = {}
      for (const k of Object.keys(SEGMENTS)) {
        payload[k] = {
          discount_pct: num(config[k].discount_pct, 0, 100),
          min_order:    num(config[k].min_order, 0, 9999999),
          note:         String(config[k].note || '').slice(0, 100),
        }
      }
      await apiFetch('/api/settings/rfm-discounts', { method: 'PATCH', body: payload })
      onFlash?.('Avantages par segment sauvegardés', 'success')
    } catch (e) {
      onFlash?.(e?.message || 'Erreur sauvegarde', 'error')
    } finally {
      setSaving(false)
    }
  }

  async function saveCriteria() {
    setSavingCriteria(true)
    try {
      await apiFetch('/api/settings/rfm-criteria', { method: 'PATCH', body: criteria })
      onFlash?.('Critères RFM sauvegardés', 'success')
    } catch (e) {
      onFlash?.(e?.message || 'Erreur sauvegarde critères', 'error')
    } finally {
      setSavingCriteria(false)
    }
  }

  if (loading) return <div className="p-6 text-sm text-slate-500">Chargement…</div>

  return (
    <div className="space-y-10 p-6">

      {/* ── SECTION 1 : Critères RFM ─────────────────────────────────────── */}
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-black text-slate-900">Critères de scoring RFM</h2>
          <p className="mt-1 text-sm text-slate-500">
            Définissez les seuils qui déterminent le score (1 à 4) de chaque client sur les 3 dimensions.
            Ces seuils décident dans quel segment chaque client est placé.
          </p>
        </div>

        {/* Explication scores */}
        <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-600">
          <div className="font-black text-slate-900 mb-2">Comment les scores fonctionnent</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-xs">
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-2.5 text-center">
              <div className="text-xl mb-0.5">🥇</div>
              <div className="font-black text-amber-700">Score 4</div>
              <div className="text-slate-500">Meilleur</div>
            </div>
            <div className="rounded-xl bg-slate-100 border border-slate-200 p-2.5 text-center">
              <div className="text-xl mb-0.5">🥈</div>
              <div className="font-black text-slate-600">Score 3</div>
              <div className="text-slate-500">Bien</div>
            </div>
            <div className="rounded-xl bg-orange-50 border border-orange-200 p-2.5 text-center">
              <div className="text-xl mb-0.5">🥉</div>
              <div className="font-black text-orange-700">Score 2</div>
              <div className="text-slate-500">Passable</div>
            </div>
            <div className="rounded-xl bg-rose-50 border border-rose-200 p-2.5 text-center">
              <div className="text-xl mb-0.5">—</div>
              <div className="font-black text-rose-600">Score 1</div>
              <div className="text-slate-500">Faible</div>
            </div>
          </div>
          <div className="mt-3 text-[11px] text-slate-400 space-y-0.5">
            <div>• Un client obtient le score 4 si ses chiffres dépassent le seuil 🥇, le score 3 si dépasse le seuil 🥈, etc.</div>
            <div>• Les 3 scores (R + F + M) combinés déterminent le segment final (Champion, Fidèle, À risque…).</div>
          </div>
        </div>

        {/* Tableau des critères */}
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 grid grid-cols-[180px_1fr] gap-4">
            <div className="text-xs font-black text-slate-600 uppercase tracking-wide">Dimension</div>
            <div className="grid grid-cols-3 gap-3 text-xs font-black text-slate-600 uppercase tracking-wide">
              <div>Seuil 🥇 (score 4)</div>
              <div>Seuil 🥈 (score 3)</div>
              <div>Seuil 🥉 (score 2)</div>
            </div>
          </div>
          <div className="px-5">
            <CriteriaRow
              label="R — Récence"
              unit="Jours depuis le dernier achat"
              scores={{
                s4: criteria.recency.s4,
                s3: criteria.recency.s3,
                s2: criteria.recency.s2,
                hint4: `≤ ${criteria.recency.s4} jours`,
                hint3: `≤ ${criteria.recency.s3} jours`,
                hint2: `≤ ${criteria.recency.s2} jours`,
              }}
              onChange={(key, val) => updateCriteria('recency', key, val)}
            />
            <CriteriaRow
              label="F — Fréquence"
              unit="Nombre de commandes total"
              scores={{
                s4: criteria.frequency.s4,
                s3: criteria.frequency.s3,
                s2: criteria.frequency.s2,
                hint4: `≥ ${criteria.frequency.s4} commandes`,
                hint3: `≥ ${criteria.frequency.s3} commandes`,
                hint2: `≥ ${criteria.frequency.s2} commandes`,
              }}
              onChange={(key, val) => updateCriteria('frequency', key, val)}
            />
            <CriteriaRow
              label="M — Montant"
              unit="Total dépensé (XOF)"
              scores={{
                s4: criteria.monetary.s4,
                s3: criteria.monetary.s3,
                s2: criteria.monetary.s2,
                hint4: `≥ ${Number(criteria.monetary.s4).toLocaleString('fr-FR')} XOF`,
                hint3: `≥ ${Number(criteria.monetary.s3).toLocaleString('fr-FR')} XOF`,
                hint2: `≥ ${Number(criteria.monetary.s2).toLocaleString('fr-FR')} XOF`,
              }}
              onChange={(key, val) => updateCriteria('monetary', key, val)}
            />
          </div>
        </div>

        {/* Note récence inversée */}
        <div className="text-xs text-slate-400 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
          <strong className="text-amber-700">Note récence :</strong> Pour la récence, un petit nombre de jours = bon (acheté récemment).
          Le score 4 est attribué si le client a acheté dans les <strong>{criteria.recency.s4} derniers jours</strong>.
          Le score 1 est attribué s'il n'a pas acheté depuis plus de <strong>{criteria.recency.s2} jours</strong>.
        </div>

        <div className="flex justify-end">
          <button
            onClick={saveCriteria}
            disabled={savingCriteria}
            className="px-6 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-black hover:bg-blue-700 disabled:opacity-50 transition"
          >
            {savingCriteria ? 'Sauvegarde…' : 'Sauvegarder les critères RFM'}
          </button>
        </div>
      </div>

      {/* ── SECTION 2 : Avantages par segment ────────────────────────────── */}
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-black text-slate-900">Avantages par segment</h2>
          <p className="mt-1 text-sm text-slate-500">
            Configurez la remise, le panier minimum requis et la note affichée au vendeur selon le segment du client.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Object.entries(SEGMENTS).map(([key, seg]) => {
            const c = COLORS[seg.color]
            const val = config[key] || DEFAULT_SEGMENT
            return (
              <div key={key} className={`rounded-2xl border-2 p-4 ${c.border} ${c.bg}`}>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">{seg.emoji}</span>
                  <div>
                    <div className={`font-black text-sm ${c.label}`}>{seg.label}</div>
                    <div className="text-[11px] text-slate-500">{seg.desc}</div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <label className="text-xs font-semibold text-slate-600 w-36 shrink-0">Remise (%)</label>
                    <div className="flex items-center gap-2 flex-1">
                      <div className="relative flex-1">
                        <input
                          type="number" min="0" max="100" step="1"
                          value={val.discount_pct}
                          onChange={e => update(key, 'discount_pct', num(e.target.value, 0, 100))}
                          className={`w-full border border-slate-300 rounded-xl px-3 py-1.5 text-sm font-bold bg-white focus:outline-none focus:ring-2 pr-7 ${c.input}`}
                        />
                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-semibold">%</span>
                      </div>
                      <span className={`text-sm font-black w-16 ${num(val.discount_pct) > 0 ? c.label : 'text-slate-400'}`}>
                        {num(val.discount_pct) > 0 ? `−${val.discount_pct}%` : 'Aucune'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="text-xs font-semibold text-slate-600 w-36 shrink-0">
                      Panier min. (XOF)
                      <div className="text-[10px] font-normal text-slate-400">0 = toujours</div>
                    </label>
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        type="number" min="0" step="500"
                        value={val.min_order}
                        onChange={e => update(key, 'min_order', num(e.target.value, 0))}
                        className={`flex-1 border border-slate-300 rounded-xl px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 ${c.input}`}
                      />
                      <span className="text-xs text-slate-400 w-16">
                        {num(val.min_order) > 0
                          ? `≥ ${Number(val.min_order).toLocaleString('fr-FR')} XOF`
                          : 'Toujours'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="text-xs font-semibold text-slate-600 w-36 shrink-0">
                      Avantage
                      <div className="text-[10px] font-normal text-slate-400">Affiché au vendeur</div>
                    </label>
                    <input
                      type="text"
                      maxLength={80}
                      value={val.note}
                      onChange={e => update(key, 'note', e.target.value)}
                      placeholder="Ex: Livraison gratuite, Cadeau offert…"
                      className={`flex-1 border border-slate-300 rounded-xl px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 ${c.input}`}
                    />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <div className="flex justify-end">
          <button
            onClick={saveDiscounts}
            disabled={saving}
            className="px-6 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-black hover:bg-slate-800 disabled:opacity-50 transition"
          >
            {saving ? 'Sauvegarde…' : 'Sauvegarder les avantages'}
          </button>
        </div>
      </div>

      {/* ── SECTION 3 : Explication segments ─────────────────────────────── */}
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-black text-slate-900">Référence des segments</h2>
          <p className="mt-1 text-sm text-slate-500">Qui se retrouve dans chaque segment selon les critères ci-dessus.</p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="text-left px-4 py-2.5 text-slate-500 font-semibold w-32">Segment</th>
                <th className="text-left px-4 py-2.5 text-slate-500 font-semibold">Règle d'attribution</th>
                <th className="text-left px-4 py-2.5 text-slate-500 font-semibold">Exemple concret</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              <tr className="hover:bg-slate-50">
                <td className="px-4 py-2.5 font-black text-emerald-700">🥇 Champions</td>
                <td className="px-4 py-2.5 text-slate-600">Score R=4 <strong>et</strong> F≥3 <strong>et</strong> M≥3</td>
                <td className="px-4 py-2.5 text-slate-400">Acheté récemment, commande souvent, gros panier</td>
              </tr>
              <tr className="hover:bg-slate-50">
                <td className="px-4 py-2.5 font-black text-blue-700">🥈 Fidèles</td>
                <td className="px-4 py-2.5 text-slate-600">Score R≥3 <strong>et</strong> F≥3</td>
                <td className="px-4 py-2.5 text-slate-400">Achat assez récent, revient régulièrement</td>
              </tr>
              <tr className="hover:bg-slate-50">
                <td className="px-4 py-2.5 font-black text-violet-700">🌱 Nouveaux</td>
                <td className="px-4 py-2.5 text-slate-600">Score R≥3 <strong>et</strong> F=1</td>
                <td className="px-4 py-2.5 text-slate-400">Achat récent mais peu de commandes au total</td>
              </tr>
              <tr className="hover:bg-slate-50">
                <td className="px-4 py-2.5 font-black text-amber-700">⚠️ À risque</td>
                <td className="px-4 py-2.5 text-slate-600">Score R≤2 <strong>et</strong> F≥3</td>
                <td className="px-4 py-2.5 text-slate-400">Bon client par le passé, n'a plus acheté</td>
              </tr>
              <tr className="hover:bg-slate-50">
                <td className="px-4 py-2.5 font-black text-slate-600">🔄 Occasionnels</td>
                <td className="px-4 py-2.5 text-slate-600">Tous les autres cas</td>
                <td className="px-4 py-2.5 text-slate-400">Achète rarement, petits montants</td>
              </tr>
              <tr className="hover:bg-slate-50">
                <td className="px-4 py-2.5 font-black text-rose-700">💤 Perdus</td>
                <td className="px-4 py-2.5 text-slate-600">Score R=1 <strong>et</strong> F≤2</td>
                <td className="px-4 py-2.5 text-slate-400">N'achète plus depuis très longtemps</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-600 space-y-1.5">
          <div className="font-black text-slate-900 mb-2">Comment les avantages s'appliquent lors d'une vente</div>
          <div>• Quand vous sélectionnez un client, son segment apparaît automatiquement en badge dans la vente.</div>
          <div>• Si le <strong>panier minimum</strong> est atteint, un bouton <em>"Appliquer −X%"</em> apparaît pour le vendeur.</div>
          <div>• L'<strong>avantage libre</strong> (livraison gratuite, cadeau…) s'affiche comme rappel — à appliquer manuellement.</div>
          <div>• La remise reste une <strong>suggestion</strong> : le vendeur peut l'ajuster ou l'ignorer.</div>
        </div>
      </div>

    </div>
  )
}
